import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getPaymentProvider,
  createPaymentProvider,
  updatePaymentProvider,
} from "store/payments/providers/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import {ILocaleData, ModaPaymentProvidersTabs} from "configs/types";

import Button from "components/buttons/Button";
import {apiRoutes} from "../../../../configs/apiRoutes";
import ProvidersMainTab from "../../tabs/psp/providers/ProvidersMainTab";
import ProvidersLocalesTab from "../../tabs/psp/providers/ProvidersLocalesTab";
import instance from "../../../../api/apiClient";
import { Modal } from "components/Modal/Modal";
import styles from './PaymentProvidersEditModal.module.scss';
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { getChangedFields, normalizeOptionalText } from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";

const PaymentProvidersEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector((store) => store["payment-providers"].isItemFetched);
  const payload = useAppSelector((store) => store["payment-providers"].payload);
  const isItemFetching = useAppSelector((store) => store["payment-providers"].isItemFetching);
  const [localeData, setLocaleData] = useState<ILocaleData>();
  const [initialLocaleData, setInitialLocaleData] = useState<ILocaleData>();
  const [isLocalesLoading, setIsLocalesLoading] = useState(false);
  const [isLocaleSaving, setIsLocaleSaving] = useState(false);

  const [imgPreview, setImgPreview] = useState<string>("");
  const [tabs] = useState<ModaPaymentProvidersTabs[]>([
    ModaPaymentProvidersTabs.main,
    ModaPaymentProvidersTabs.local,
    // ModaPaymentProvidersTabs.restrictions,
    // ModaPaymentProvidersTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModaPaymentProvidersTabs>(
    ModaPaymentProvidersTabs.main,
  );

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const [isCreateFlow] = useState(!isEdit);
  const permissions = usePermissionHelper();
  const canEdit =
    activeTab === ModaPaymentProvidersTabs.local
      ? isCreateFlow
        ? (permissions?.create ?? false)
        : (permissions?.update ?? false)
      : isEdit
        ? (permissions?.update ?? false)
        : (permissions?.create ?? false);

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getPaymentProvider(entityId));
    }
  }, [entityId, isItemFetched]);

  useEffect(() => {
    if (payload?.image) {
      if (typeof payload.image === "string") {
        setImgPreview(payload.image);
      } else {
        const file = payload?.image;
        if (file && file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setImgPreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  }, [payload]);

  const isLoaded = !isEdit || isItemFetched;

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

const handleApply = async () => {
  if (!canEdit) return;

  if (payload && activeTab === ModaPaymentProvidersTabs.main) {
    if (!isEdit) {
      formErrors.resetErrors();
    }

    try {
      if (isEdit) {
        await dispatch(updatePaymentProvider(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const result = await dispatch(createPaymentProvider(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        navigate(
          `${location.pathname}?activeModal=paymentProvider&entityId=${result}`,
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
      return;
    }
  }

  if (activeTab === ModaPaymentProvidersTabs.local && entityId && localeData) {
    await handleSetLocale();
  }
};


  const handleTabClick = (tab: ModaPaymentProvidersTabs) => {
    if (!isEdit && tab !== ModaPaymentProvidersTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModaPaymentProvidersTabs.main) {
      return (
        <ProvidersMainTab
          payload={payload}
          handleData={handleData}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={!canEdit}
        />
      );
    }
    if (activeTab === ModaPaymentProvidersTabs.local) {
      return (
        <ProvidersLocalesTab
          id={entityId ?? undefined}
          currentData={localeData}
          handleData={setLocaleData}
          onInitialData={setInitialLocaleData}
          url={apiRoutes.paymentProvidersGetTranslations}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          onLoadingChange={setIsLocalesLoading}
          isReadOnly={!canEdit}
        />
      );
    }
    return null;
  };

const handleSetLocale = async () => {
  if (!canEdit || !localeData || !entityId) return;

  const textChanges = getChangedFields(
    initialLocaleData,
    localeData,
    ["title", "content", "isActive"],
    {
      title: normalizeOptionalText,
      content: normalizeSeoHtml,
      isActive: Boolean,
    },
  );
  if (!Object.keys(textChanges).length) return;

  setIsLocaleSaving(true);
  try {
    const { content, isActive, ...otherChanges } = textChanges;
    await instance.put(
      `${apiRoutes.paymentProvidersSetTranslations}/${entityId}`,
      {
        locale: localeData.locale,
        ...otherChanges,
        ...(content !== undefined ? { description: content } : {}),
        ...(isActive !== undefined ? { isActive: isActive ? 1 : 0 } : {}),
      },
    );
    setInitialLocaleData(localeData);

    dispatch(
      showNotification({
        title: t("success"),
        message: t("entityUpdateSuccess"),
        type: NotificationTypes.success,
      }),
    );
  } catch (error) {
    handleApiError({
      error,
      t,
      dispatch,
      formErrors: {
        setFieldError: (field, code) =>
          formErrors.setFieldError(
            field === "description" ? "content" : field,
            code,
          ),
      },
    });
  } finally {
    setIsLocaleSaving(false);
  }
};
  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("paymentProvider")}</Modal.Header>

      {!isLoaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={`${styles.paymentProviderContent} ${
              activeTab === ModaPaymentProvidersTabs.main
                ? styles.mainTabContent
                : ""
            }`}
          >
            <div className="bonus-content payment-content">{renderTab()}</div>
          </Modal.Content>

          {canEdit && <Modal.Footer>
            {activeTab === ModaPaymentProvidersTabs.local && isLocalesLoading ? (
              <ModalFooterSkeleton />
            ) : (
              <>
                <Button
                  isSecondary
                  txt={t("cancel")}
                  handleClick={handleCloseClick}
                />

                <Button
                  isPrimary
                  handleClick={handleApply}
                  txt={t("save")}
                  isLoad={isItemFetching || isLocaleSaving}
                />
              </>
            )}
          </Modal.Footer>}
        </>
      )}
    </Modal>
  );
};

export default PaymentProvidersEditModal;
